import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, CheckCircle, XCircle, CreditCard } from 'lucide-react';
import SystemPayForm from '../components/SystemPayForm';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function SubscriptionCheckoutPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { formToken, subscriptionId, planName, price } = location.state || {};
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    if (!formToken) {
      toast.error('Session de paiement invalide');
      navigate('/tarifs-lavage');
    }
  }, [formToken, navigate]);

  const handlePaid = useCallback(async () => {
    setPaymentStatus('success');
    try {
      await fetch(`${API}/subscriptions/confirm?subscription_id=${subscriptionId}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (e) { console.error(e); }
    toast.success('Abonnement active !');
  }, [subscriptionId, token]);

  const handleFailed = useCallback(() => setPaymentStatus('error'), []);

  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-4">
        <Card className="bg-zinc-900/50 border-zinc-800 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <CheckCircle className="mx-auto text-green-500 mb-4" size={64} />
            <h2 className="text-2xl font-bold text-white mb-2">Abonnement active !</h2>
            <p className="text-zinc-400 mb-6">Votre carte {planName} est desormais active.</p>
            <Button onClick={() => navigate('/mon-compte')} className="bg-red-600 hover:bg-red-700 w-full" data-testid="sub-success-account">
              Voir mon espace
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (paymentStatus === 'error') {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-4">
        <Card className="bg-zinc-900/50 border-zinc-800 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="mx-auto text-red-500 mb-4" size={64} />
            <h2 className="text-2xl font-bold text-white mb-2">Echec du paiement</h2>
            <p className="text-zinc-400 mb-6">Veuillez reessayer.</p>
            <Button onClick={() => navigate('/tarifs-lavage')} variant="outline" className="border-zinc-700 w-full" data-testid="sub-retry">
              Retour aux abonnements
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] py-12">
      <div className="max-w-2xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate('/tarifs-lavage')} className="text-zinc-400 mb-6" data-testid="sub-back">
          <ArrowLeft size={18} className="mr-2" /> Retour aux abonnements
        </Button>
        <h1 className="text-3xl font-bold text-white mb-8">
          <CreditCard className="inline mr-3 text-red-500" size={32} /> PAIEMENT ABONNEMENT
        </h1>

        <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
          <CardHeader><CardTitle className="text-white">Recapitulatif</CardTitle></CardHeader>
          <CardContent>
            <div className="flex justify-between items-center pt-2 border-t border-zinc-700">
              <div>
                <p className="text-lg text-white font-bold">{planName}</p>
                <p className="text-sm text-zinc-400">Abonnement</p>
              </div>
              <p className="text-2xl text-red-500 font-bold" data-testid="sub-total">{Number(price || 0).toFixed(2)} EUR</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <CreditCard className="text-red-500" size={20} /> Paiement securise
            </CardTitle>
          </CardHeader>
          <CardContent>
            {formToken && (
              <SystemPayForm
                formToken={formToken}
                amount={Number(price || 0)}
                onPaid={handlePaid}
                onFailed={handleFailed}
                testId="subscription-systempay-form"
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
